import React from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

import Modal from "../../../layouts/Modal";
import { ISellerSoldHistory } from "../../../models/SellerHistory/SellerSoldHistory";
import { compatibilityColumns, prettyColumn } from "./partDetails";

interface PartDetailsModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  part: ISellerSoldHistory | null;
}

export default function PartDetailsModal({
  open,
  setOpen,
  part,
}: PartDetailsModalProps) {
  const specifics = part?.itemSpecifics ?? [];
  const compat = part?.compatibility ?? null;
  const compatColumns = compatibilityColumns(compat);
  const hasSpecifics = specifics.length > 0;
  const hasCompat = compatColumns.length > 0;

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-4xl">
      <div className="flex items-start justify-between border-b pb-3">
        <div className="pr-4">
          <h3 className="text-lg font-medium text-gray-900">Part details</h3>
          {part?.title ? (
            <p className="mt-1 text-sm text-gray-500 line-clamp-2" title={part.title}>
              {part.title}
            </p>
          ) : null}
          {part?.lastRevised ? (
            <p className="mt-1 text-xs text-gray-400">
              Last revised: {part.lastRevised}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          onClick={() => setOpen(false)}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-4 max-h-[70vh] space-y-6 overflow-y-auto">
        {/* Item specifics */}
        <section>
          <h4 className="mb-2 text-sm font-semibold text-gray-700">
            Item specifics {hasSpecifics ? `(${specifics.length})` : ""}
          </h4>
          {hasSpecifics ? (
            <dl className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
              {specifics.map((spec) => (
                <div
                  key={spec.name}
                  className="flex justify-between gap-4 border-b border-gray-100 py-1 text-sm"
                >
                  <dt className="text-gray-500">{spec.name}</dt>
                  <dd className="text-right font-medium text-gray-900">
                    {spec.value}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-sm text-gray-400">No item specifics recorded.</p>
          )}
        </section>

        {/* Compatibility */}
        <section>
          <h4 className="mb-2 text-sm font-semibold text-gray-700">
            Compatibility {hasCompat ? `(${compat?.items.length})` : ""}
          </h4>
          {compat?.summary ? (
            <p className="mb-2 text-sm text-gray-600">{compat.summary}</p>
          ) : null}

          {hasCompat ? (
            <div className="overflow-x-auto rounded border border-gray-200">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 text-left text-gray-600">
                    {compatColumns.map((col) => (
                      <th key={col} className="whitespace-nowrap px-3 py-2">
                        {prettyColumn(col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {compat!.items.map((row, idx) => (
                    <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                      {compatColumns.map((col) => (
                        <td key={col} className="px-3 py-2 align-top text-gray-800">
                          {row[col] ?? ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-400">No compatibility list recorded.</p>
          )}

          {compat?.note ? (
            <p className="mt-2 text-xs italic text-gray-400">{compat.note}</p>
          ) : null}
        </section>
      </div>
    </Modal>
  );
}
